import { useCallback, useRef, useState } from 'react'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'
import { outcomeOf, PRINTER_FAILURES, relaunchPrint } from './labelPrintingApi'
import { OUTCOME_TEXT, PROBLEM_TEXT, textOf } from './labelPrintingTexts'

const CLOSED_NOTICE = { open: false, severity: 'success', lines: [], retry: null }
const CLOSED_QUESTION = { open: false, text: '', confirm: null }

// Historia 226 (CU6, CU7; RN25, RN26, RN33, RN37): what happens after asking Sun to print or relaunch a label, shared
// by the QR dialog and the History tab.
//   - `send(acceptTextTruncation)` performs the request (print or relaunch);
//   - TEXT_TRUNCATION_NOT_CONFIRMED asks "N lines will be cut"; "Print anyway" repeats `send(true)`;
//   - a printer failure (409) shows a persistent notice whose Retry relaunches THAT record with the same flag;
//   - every other outcome is a notice without Retry. Jupiter branches on `outcome`, never on Sun's `reason`.
// `onSettled(response, error, context)` runs once per finished attempt (not for the truncation question), so the caller
// can update the printer's chip or reload the history; `context` is what the caller passed to `attempt` (e.g. the
// printer of the attempt), kept for the confirmation and for every Retry of it.
const useLabelPrintFlow = ({ onSettled = () => {} } = {}) => {
  const { t_ } = useSmartTranslation()
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [notice, setNotice] = useState(CLOSED_NOTICE)
  const [question, setQuestion] = useState(CLOSED_QUESTION)

  const failureLines = useCallback((data, printerName) => {
    const outcome = data.outcome || ''
    if (PRINTER_FAILURES.includes(outcome)) {
      return [
        t_('The label was not printed'),
        t_(OUTCOME_TEXT[outcome]),
        ...(data.problems || []).map((problem) => t_(textOf(PROBLEM_TEXT, problem))),
        ...(data.labelSent
          ? [t_('The label reached the printer and may come out when the problem is solved; retrying may print it twice.')]
          : [])
      ]
    }
    if (outcome === 'QR_DOES_NOT_FIT') {
      return [t_('The QR does not fit on the label of {{printer}}. Remove fields from the QR or use a bigger label.', { printer: printerName })]
    }
    return [t_(OUTCOME_TEXT[outcome] || 'Unexpected error')]
  }, [t_])

  const attempt = useCallback(async (send, printerName, acceptTextTruncation = false, context = {}) => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setNotice(CLOSED_NOTICE)
    try {
      const response = await send(acceptTextTruncation)
      const unresolved = response.unresolvedFields || []
      setNotice({
        open: true,
        severity: 'success',
        lines: [
          t_('Label printed on {{printer}}', { printer: response.printerName }),
          ...(unresolved.length > 0 ? [t_('Fields not printed: {{fields}}', { fields: unresolved.join(', ') })] : [])
        ],
        retry: null
      })
      onSettled(response, {}, context)
    } catch (error) {
      const data = error?.data || {}
      if (outcomeOf(error) === 'TEXT_TRUNCATION_NOT_CONFIRMED') {
        setQuestion({
          open: true,
          text: t_('Not all the text fits on the label of {{printer}}: {{count}} lines will be cut.', {
            printer: printerName,
            count: data.linesDropped
          }),
          confirm: () => attempt(send, printerName, true, context)
        })
      } else {
        const retryable = PRINTER_FAILURES.includes(outcomeOf(error)) && Boolean(data.printId)
        setNotice({
          open: true,
          severity: 'error',
          lines: failureLines(data, printerName),
          retry: retryable
            ? () => attempt(context.retrySend || ((accept) => relaunchPrint(data.printId, accept)), printerName, acceptTextTruncation, context)
            : null
        })
        onSettled({}, error || {}, context)
      }
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [t_, failureLines, onSettled])

  // A failure that happens before asking Sun (e.g. saving the template) uses the same notice.
  const fail = useCallback((error) => {
    setNotice({ open: true, severity: 'error', lines: [t_(OUTCOME_TEXT[outcomeOf(error)] || 'Unexpected error')], retry: null })
  }, [t_])

  const answer = useCallback((confirmed) => {
    const { confirm } = question
    setQuestion(CLOSED_QUESTION)
    if (confirmed && confirm) confirm()
  }, [question])

  const closeNotice = useCallback(() => setNotice((current) => ({ ...current, open: false })), [])

  // While the save before printing runs, the flow is already busy (S132: a second click does nothing).
  const hold = useCallback(() => {
    if (busyRef.current) return false
    busyRef.current = true
    setBusy(true)
    return true
  }, [])
  const release = useCallback(() => {
    busyRef.current = false
    setBusy(false)
  }, [])

  return { busy, notice, question, attempt, fail, answer, closeNotice, hold, release }
}

export default useLabelPrintFlow
